import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import StatusChip from '../../components/ui/StatusChip';

const QA_BADGE = {
  pending:  'bg-amber-100 text-amber-700 border border-amber-200',
  approved: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border border-red-200',
};

const QUALITY_RESULT_BADGE = {
  Passed: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  'Partially Passed': 'bg-amber-100 text-amber-700 border border-amber-200',
  Rejected: 'bg-red-100 text-red-700 border border-red-200',
};

// Derived label — no new stored status; computed from the existing qaStatus + item quantities.
const qualityResult = (g) => {
  if (!g || g.qaStatus === 'pending') return null;
  if (g.qaStatus === 'rejected') return 'Rejected';
  const hasIssue = (g.items || []).some(
    (it) => (it.damagedQty || 0) > 0 || (it.missingQty || 0) > 0 || it.approved === false
  );
  return hasIssue ? 'Partially Passed' : 'Passed';
};

// Per-item condition, derived from the existing quantity fields — no new stored status.
const itemCondition = (it) => {
  const parts = [];
  if ((it.damagedQty || 0) > 0) parts.push('Damaged');
  if ((it.missingQty || 0) > 0) parts.push('Missing');
  return parts.length ? parts.join(' + ') : 'Correct';
};

const CONDITION_CHIP = {
  Correct: 'chip-green',
  Damaged: 'chip-red',
  Missing: 'chip-red',
};

const GRNDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qaAction, setQaAction] = useState('');
  const [qaComment, setQaComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Indices of the items ticked for approval. Pre-ticked with the lines that have
  // no damaged or missing quantity, which is the common case.
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get(`/grn/${id}`)
      .then((r) => {
        const data = r.data.data;
        setGrn(data);
        setSelected(
          (data.items || [])
            .map((it, i) => (itemCondition(it) === 'Correct' ? i : null))
            .filter((i) => i !== null)
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const toggleItem = (idx) =>
    setSelected((prev) => (prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]));

  const handleQAReview = async () => {
    if (!qaAction) return toast.error('Select Approve or Reject');
    if (!qaComment.trim()) return toast.error('QA comment is mandatory');
    if (qaAction === 'approved' && selected.length === 0) {
      return toast.error('Tick at least one item to approve, or reject the GRN instead');
    }
    setSubmitting(true);
    try {
      const payload = { action: qaAction, comment: qaComment };
      // Item-level decisions are only meaningful on approval; a rejection applies
      // to the whole GRN, exactly as before.
      if (qaAction === 'approved') {
        payload.items = (grn.items || []).map((it, i) => ({
          index: i,
          partName: it.partName,
          approved: selected.includes(i),
        }));
      }
      const res = await api.patch(`/grn/${id}/review`, payload);
      setGrn(res.data.data);
      toast.success(`GRN ${qaAction === 'approved' ? 'approved' : 'rejected'} successfully`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'QA review failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => <div key={i} className="zip-card h-32 skeleton" />)}
    </div>
  );
  if (!grn) return <div className="text-center text-zip-muted py-24">GRN not found</div>;

  const inward = grn.inwardReceipt;
  const canReview = can('grn.review') && grn.status === 'generated' && grn.qaStatus === 'pending';

  return (
    <div className="space-y-5 max-w-5xl">
      <button onClick={() => navigate('/grn')}
        className="flex items-center gap-2 text-sm text-zip-muted hover:text-zip-text transition-colors">
        <ArrowLeft size={15} /> Back to GRN List
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Items table */}
        <div className="lg:col-span-2 space-y-5">
          <div className="zip-card overflow-hidden">
            <div className="px-5 py-4 border-b border-zip-border flex items-center justify-between">
              <h3 className="font-semibold text-zip-text tracking-tight">GRN Items</h3>
              <span className="font-mono text-xs text-zip-red">{grn.grnNumber}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-zip-surface border-b border-zip-border">
                    {canReview && <th className="zip-th w-10">Select</th>}
                    {['Part No.', 'Part Name', 'Received Qty', 'OK', 'Damaged', 'Missing', 'Status', 'Cause', 'Boxes'].map((h) => (
                      <th key={h} className="zip-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grn.items.map((item, i) => (
                    <tr
                      key={i}
                      className={`border-b border-zip-border last:border-0 ${
                        canReview && !selected.includes(i) ? 'opacity-50' : ''
                      }`}
                    >
                      {canReview && (
                        <td className="zip-td">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                            checked={selected.includes(i)}
                            onChange={() => toggleItem(i)}
                            aria-label={`Approve ${item.partName}`}
                          />
                        </td>
                      )}
                      <td className="zip-td font-mono text-xs text-zip-muted">{item.partNo || '—'}</td>
                      <td className="zip-td font-medium">{item.partName}</td>
                      <td className="zip-td font-mono">
                        {item.totalQuantity}
                        <span className="text-zip-muted text-xs ml-1">{item.uom || 'PCS'}</span>
                      </td>
                      <td className="zip-td">
                        {item.okQty ? <span className="chip-green">{item.okQty}</span> : <span className="text-zip-muted text-xs">—</span>}
                      </td>
                      <td className="zip-td">
                        {item.damagedQty ? <span className="chip-red">{item.damagedQty}</span> : <span className="text-zip-muted text-xs">—</span>}
                      </td>
                      <td className="zip-td">
                        {item.missingQty ? <span className="chip-red">{item.missingQty}</span> : <span className="text-zip-muted text-xs">—</span>}
                      </td>
                      <td className="zip-td">
                        <span className={CONDITION_CHIP[itemCondition(item)] || 'chip-red'}>
                          {itemCondition(item)}
                        </span>
                        {item.approved === false && (
                          <span className="chip-gray ml-1">Not approved</span>
                        )}
                      </td>
                      <td className="zip-td text-xs text-zip-text">{item.cause || <span className="text-zip-muted">—</span>}</td>
                      <td className="zip-td">
                        {item.boxes?.length > 0 ? (
                          <div className="space-y-0.5">
                            {item.boxes.map((b, bi) => (
                              <div key={bi} className="text-xs font-mono text-zip-muted">
                                Box {b.boxNumber}: {b.boxQuantity} {(item.uom || 'PCS').toLowerCase()}
                              </div>
                            ))}
                          </div>
                        ) : <span className="text-zip-muted text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional details */}
          {(grn.materialTestCertNo || grn.ewayBillNo || grn.materialLocation || grn.materialCountedBy || grn.remarks) && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">Additional Details</h3>
              {[
                grn.materialTestCertNo && ['Material Test Cert No.', grn.materialTestCertNo],
                grn.ewayBillNo         && ['Eway Bill No.',          grn.ewayBillNo],
                grn.materialLocation   && ['Material Location',      grn.materialLocation],
                grn.materialCountedBy  && ['Counted By',             grn.materialCountedBy],
                grn.remarks            && ['Remarks',                grn.remarks],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between text-sm gap-4">
                  <span className="text-zip-muted flex-shrink-0">{k}</span>
                  <span className="text-zip-text font-mono text-xs text-right">{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* QA Review form */}
          {canReview && (
            <div className="zip-card p-5 space-y-4">
              <h3 className="font-semibold text-zip-text tracking-tight">Quality Assurance Review</h3>
              <div className="flex gap-3">
                <button type="button"
                  onClick={() => setQaAction('approved')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all
                    ${qaAction === 'approved' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-zip-border text-zip-muted hover:border-emerald-400'}`}>
                  <CheckCircle size={15} /> Approve
                </button>
                <button type="button"
                  onClick={() => setQaAction('rejected')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all
                    ${qaAction === 'rejected' ? 'bg-zip-red text-white border-zip-red' : 'border-zip-border text-zip-muted hover:border-zip-red/50'}`}>
                  <XCircle size={15} /> Reject
                </button>
              </div>
              {qaAction === 'approved' && (
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 space-y-1">
                  <p className="text-emerald-800 font-medium">
                    {selected.length} of {grn.items.length} item(s) ticked for approval.
                  </p>
                  <ul className="text-emerald-700/90 list-disc pl-4 space-y-0.5">
                    <li>Only ticked items are approved and can be put away into stock.</li>
                    <li>Damaged quantity moves to Quarantine automatically.</li>
                    <li>Missing quantity is not treated as received.</li>
                    <li>Unticked items are held in Quarantine, not approved.</li>
                  </ul>
                </div>
              )}
              {qaAction === 'rejected' && (
                <p className="text-xs text-zip-red bg-zip-red/5 border border-zip-red/20 rounded-lg px-3 py-2">
                  Rejecting applies to the whole GRN — all items will be sent to Quarantine automatically.
                </p>
              )}
              <div>
                <label className="block text-xs font-medium text-zip-muted mb-1.5">Comment * <span className="text-zip-muted/60 font-normal">(mandatory)</span></label>
                <textarea
                  className="zip-input min-h-[80px] resize-none"
                  placeholder="Enter QA comment…"
                  value={qaComment}
                  onChange={(e) => setQaComment(e.target.value)}
                />
              </div>
              <button type="button" onClick={handleQAReview} disabled={submitting || !qaAction}
                className="btn-primary flex items-center gap-2">
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting…</>
                ) : 'Submit QA Review'}
              </button>
            </div>
          )}

          {/* QA result display */}
          {grn.qaStatus !== 'pending' && (
            <div className={`zip-card p-5 space-y-2 border-l-4 ${grn.qaStatus === 'approved' ? 'border-l-emerald-500' : 'border-l-zip-red'}`}>
              <h3 className="font-semibold text-zip-text tracking-tight">Quality Assurance Decision</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${QUALITY_RESULT_BADGE[qualityResult(grn)]}`}>
                  {qualityResult(grn)}
                </span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${QA_BADGE[grn.qaStatus]}`}>
                  {grn.qaStatus}
                </span>
                <span className="text-xs text-zip-muted">by {grn.qaBy?.name} on {grn.qaAt && format(new Date(grn.qaAt), 'dd MMM yyyy HH:mm')}</span>
              </div>
              <p className="text-sm text-zip-text">{grn.qaComment}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="zip-card p-5 space-y-3">
            <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">GRN Info</h3>
            {[
              ['GRN No.',    <span className="font-mono text-zip-red text-xs">{grn.grnNumber}</span>],
              ['Status',     <StatusChip status={grn.status} />],
              ['QA Status',  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${QA_BADGE[grn.qaStatus]}`}>{grn.qaStatus}</span>],
              ...(qualityResult(grn) ? [['Quality Result', <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${QUALITY_RESULT_BADGE[qualityResult(grn)]}`}>{qualityResult(grn)}</span>]] : []),
              ['Client',     grn.client?.name],
              ['Items',      <span className="font-mono">{grn.items?.length}</span>],
              ['Created',    format(new Date(grn.createdAt), 'dd MMM yyyy HH:mm')],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-zip-muted">{k}</span>
                <span className="text-zip-text font-medium">{v}</span>
              </div>
            ))}
          </div>

          {inward && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">Inward Details</h3>
              {[
                ['Inward No.',  <span className="font-mono text-xs text-amber-600">{inward.inwardNumber}</span>],
                ['Invoice No.', <span className="font-mono text-xs">{inward.invoiceNumber}</span>],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-zip-muted">{k}</span>
                  <span className="text-zip-text font-medium">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GRNDetail;
